import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import apiService from "../Common/Configurations/APIService";
import config from "../Common/Configurations/APIConfig";
import countriesData from "../Data/Countries.json";
import currencyFlags from "../Data/CurrencyFlags.json";
import { HomePageSlider } from "./HomePageSlider";

export default function SaasRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [countries, setCountries] = useState([]);

  const [basicValues, setBasicValues] = useState({
    companyName: "",
    email: "",
    phoneNumber: "",
    country: "IN",
    currency: "INR",
    password: "",
    companyType: "CLIENT",
    userType: "CLIENT",
    planType: "TRAIL",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    // Process countries data from CurrencyFlags.json
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );

      setCountries(sortedCountries);

      // Set default country (India)
      const india = sortedCountries.find(
        (country) => country.countryCode === "IN"
      );
      if (india) {
        setBasicValues((prev) => ({
          ...prev,
          country: "IN",
          currency: india.code || "INR",
        }));
      }
    }
  }, [searchParams]);

  const handleBasicChange = (e) => {
    const { name, value } = e.target;

    if (name === "country") {
      // When country changes, update both country and currency
      const selectedCountry = countries.find(
        (country) => country.countryCode === value
      );
      setBasicValues({
        ...basicValues,
        country: value,
        currency: selectedCountry?.code || "",
      });
    } else {
      setBasicValues({ ...basicValues, [name]: value });
    }

    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: "" });
    }
    if (apiError) {
      setApiError("");
    }
  };

  const validateBasicForm = () => {
    let temp = {};
    temp.companyName = basicValues.companyName !== "";
    temp.email =
      basicValues.email !== "" && /\S+@\S+\.\S+/.test(basicValues.email);
    temp.phoneNumber = basicValues.phoneNumber !== "";
    temp.country = basicValues.country !== "";
    temp.password =
      basicValues.password !== "" && basicValues.password.length >= 6;

    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const applyErrorClass = (field) =>
    (field in errors && errors[field] === false) || fieldErrors[field]
      ? " form-control-danger"
      : "";

  const handleBasicSubmit = async (e) => {
    e.preventDefault();
    if (!validateBasicForm()) return;

    setLoading(true);
    setApiError("");
    setFieldErrors({});

    try {
      // Prepare the request object matching the backend CompanyRequest model
      const requestData = {
        CompanyName: basicValues.companyName,
        Email: basicValues.email,
        PhoneNumber: basicValues.phoneNumber,
        Country: basicValues.country,
        Currency: basicValues.currency,
        Password: basicValues.password,
        CompanyType: basicValues.companyType,
        UserType: basicValues.userType,
        PlanType: basicValues.planType,
        ParentCompanyId: config.DEFAULTCOMPANYID,
        DomainUrl: window.location.origin,
      };

      console.log("Sending registration data:", requestData);

      const response = await apiService.create(
        config.REGISTERCOMPANY,
        requestData
      );

      console.log("Registration response:", response);

      // Simplified success handling - check the actual response structure
      if (response && response.statusCode === 200) {
        // Check if we have the nested success response
        if (response.response && response.response.success) {
          console.log("Registration successful, navigating to pending page");
          navigate("/registration-pending", {
            replace: true,
            state: {
              userEmail: basicValues.email, // Make sure to pass the email
              message: response.response.message,
              companyId: response.response.companyId,
            },
          });
          return;
        }
      }
    } catch (error) {}
  };

  return (
    <div className="auth-page-wrapper py-5 d-flex justify-content-center align-items-center min-vh-100">
      <div className="bg-overlay" style={{ backgroundColor: "#F2F44E" }}></div>
      <div className="auth-page-content overflow-hidden mt-lg-4">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="card overflow-hidden">
                <div className="row g-0">
                  <div className="col-lg-6">
                    <div className="card overflow-hidden rounded-4 loginbox shadow">
                      <div className="p-lg-5 p-4">
                        {step === 1 && (
                          <>
                            <h3 className="text-center">REGISTRATION</h3>
                            <form
                              onSubmit={handleBasicSubmit}
                              autoComplete="off"
                              noValidate
                            >
                              {/* Company Information */}
                              <div className="mb-3">
                                <label className="form-label">
                                  Company Name*
                                </label>
                                <input
                                  type="text"
                                  className={
                                    "form-control" +
                                    applyErrorClass("companyName")
                                  }
                                  name="companyName"
                                  value={basicValues.companyName}
                                  onChange={handleBasicChange}
                                  placeholder="Enter Company Name"
                                  required
                                />
                                {errors.companyName === false && (
                                  <div className="validationerror">
                                    Please enter company name
                                  </div>
                                )}
                                {fieldErrors.companyName && (
                                  <div className="validationerror">
                                    {fieldErrors.companyName}
                                  </div>
                                )}
                              </div>

                              <div className="mb-3">
                                <label className="form-label">Email*</label>
                                <input
                                  type="email"
                                  className={
                                    "form-control" + applyErrorClass("email")
                                  }
                                  name="email"
                                  value={basicValues.email}
                                  onChange={handleBasicChange}
                                  placeholder="Enter Email"
                                  required
                                />
                                {errors.email === false && (
                                  <div className="validationerror">
                                    Please enter valid email
                                  </div>
                                )}
                                {fieldErrors.email && (
                                  <div className="validationerror">
                                    {fieldErrors.email}
                                  </div>
                                )}
                              </div>

                              <div className="mb-3">
                                <label className="form-label">
                                  Phone Number*
                                </label>
                                <input
                                  type="text"
                                  className={
                                    "form-control" +
                                    applyErrorClass("phoneNumber")
                                  }
                                  name="phoneNumber"
                                  value={basicValues.phoneNumber}
                                  onChange={handleBasicChange}
                                  placeholder="Enter Phone Number"
                                  maxLength={15}
                                  required
                                />
                                {errors.phoneNumber === false && (
                                  <div className="validationerror">
                                    Please enter phone number
                                  </div>
                                )}
                              </div>

                              <div className="mb-3">
                                <label className="form-label">Country*</label>
                                <select
                                  className={
                                    "form-select" + applyErrorClass("country")
                                  }
                                  name="country"
                                  value={basicValues.country}
                                  onChange={handleBasicChange}
                                  required
                                >
                                  <option value="">Select Country</option>
                                  {countries.map((country) => (
                                    <option
                                      key={country.countryCode}
                                      value={country.countryCode}
                                    >
                                      {country.flag && (
                                        <img
                                          src={country.flag}
                                          alt={country.country}
                                          style={{
                                            width: "20px",
                                            height: "15px",
                                            marginRight: "8px",
                                            verticalAlign: "middle",
                                          }}
                                        />
                                      )}
                                      {country.country}
                                    </option>
                                  ))}
                                </select>
                                {errors.country === false && (
                                  <div className="validationerror">
                                    Please select a country
                                  </div>
                                )}
                              </div>

                              <div className="mb-3">
                                <label className="form-label">Password*</label>
                                <div className="position-relative auth-pass-inputgroup mb-1">
                                  <input
                                    type={showPassword ? "text" : "password"}
                                    className={
                                      "form-control" +
                                      applyErrorClass("password")
                                    }
                                    name="password"
                                    value={basicValues.password}
                                    onChange={handleBasicChange}
                                    placeholder="Enter Password (min. 6 characters)"
                                    required
                                  />
                                  <span
                                    className="position-absolute"
                                    style={{
                                      right: "10px",
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      cursor: "pointer",
                                    }}
                                    onClick={togglePasswordVisibility}
                                  >
                                    {showPassword ? (
                                      <i className="ri-eye-line"></i>
                                    ) : (
                                      <i className="ri-eye-off-line"></i>
                                    )}
                                  </span>
                                </div>
                                {errors.password === false && (
                                  <div className="validationerror">
                                    Password must be at least 6 characters
                                  </div>
                                )}
                              </div>

                              {/* Show general API errors */}
                              {apiError && (
                                <div className="alert alert-danger">
                                  {apiError}
                                </div>
                              )}

                              <div className="mt-4 d-flex justify-content-end">
                                {!loading ? (
                                  <button
                                    type="submit"
                                    className="btn btn-success w-100 me-2"
                                  >
                                    Register Now
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-primary w-100 me-2"
                                    disabled
                                  >
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Registering...
                                  </button>
                                )}
                                <Link
                                  to="/"
                                  className="btn btn-danger me-2 w-100"
                                >
                                  Back
                                </Link>
                              </div>
                            </form>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <HomePageSlider />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
